/** Pack identities shared by the shop, starters, and inventory JSON. */

import type { PrintedRarity } from './printed'
import {
  PLAYTIME_PACK_TOP,
  PROMO_MRD_PACK_ID,
  SEASON_START_PACK_ID,
} from './expansion'

export const PACK_IDS = [
  'starter-beasts',
  'starter-runes',
  'starter-crucible',
  'shop-onyx',
  'set-lob',
  'promo-lob',
  'set-mrd',
  'promo-mrd',
] as const

export type PackId = (typeof PACK_IDS)[number]

export type PackKind = 'starter' | 'shop' | 'promo'

export type PackPool = 'main-monster' | 'spell-trap' | 'extra' | 'all'

export type PackLogicDef = {
  id: PackId
  size: number
  kind: PackKind
  pool: PackPool
  /** Album prefix. When set, the pack only pulls cards from that set. */
  set?: string
  /**
   * Printings that admit a card. LOB is LOB + the Yugi and Kaiba starters.
   * Metal Raiders is MRD only. A card's slot uses its highest rarity.
   */
  sets?: readonly string[]
  /** 2002 booster: 8 Commons and one Rare-or-better. */
  booster?: boolean
  /** Cards below this printed rarity are left out. */
  minRarity?: PrintedRarity
}

export const PACK_LOGIC: PackLogicDef[] = [
  {
    id: 'starter-beasts',
    size: 20,
    kind: 'starter',
    pool: 'main-monster',
  },
  {
    id: 'starter-runes',
    size: 20,
    kind: 'starter',
    pool: 'spell-trap',
  },
  {
    id: 'starter-crucible',
    size: 5,
    kind: 'starter',
    pool: 'extra',
  },
  {
    id: 'shop-onyx',
    size: 5,
    kind: 'shop',
    pool: 'all',
  },
  {
    id: 'set-lob',
    size: 9,
    kind: 'shop',
    pool: 'all',
    sets: ['LOB', 'SDY', 'SDK'],
    booster: true,
  },
  {
    id: 'promo-lob',
    size: 1,
    kind: 'promo',
    pool: 'all',
  },
  {
    id: 'set-mrd',
    size: 9,
    kind: 'shop',
    pool: 'all',
    sets: ['MRD'],
    booster: true,
  },
  {
    id: 'promo-mrd',
    size: 5,
    kind: 'promo',
    pool: 'all',
    sets: ['MRD'],
    minRarity: 'ultra',
  },
]

export const PACK_LOGIC_BY_ID: Record<PackId, PackLogicDef> = Object.fromEntries(
  PACK_LOGIC.map((pack) => [pack.id, pack]),
) as Record<PackId, PackLogicDef>

export const STARTER_PACK_IDS: PackId[] = PACK_LOGIC.filter(
  (pack) => pack.kind === 'starter',
).map((pack) => pack.id)

/** Master Pack. Grants and the welcome gift still use this. The shop does not. */
export const SHOP_PACK_ID: PackId = 'shop-onyx'

/** Packs the shop sells. Both cost {@link SHOP_PACK_PRICE}. */
export const SHOP_SELL_IDS = ['set-lob', 'set-mrd'] as const

export type ShopSellId = (typeof SHOP_SELL_IDS)[number]

export function isShopSellId(value: unknown): value is ShopSellId {
  return (
    typeof value === 'string' &&
    (SHOP_SELL_IDS as readonly string[]).includes(value)
  )
}

/** Five-card shop boosters gifted with a new account. */
export const WELCOME_SHOP_PACKS = 3

export { PLAYTIME_PACK_TOP, PROMO_MRD_PACK_ID, SEASON_START_PACK_ID }

export function isPackId(value: unknown): value is PackId {
  return typeof value === 'string' && (PACK_IDS as readonly string[]).includes(value)
}

/**
 * Zero unopened packs of every type.
 */
export function emptyPackCounts(): Record<PackId, number> {
  return {
    'starter-beasts': 0,
    'starter-runes': 0,
    'starter-crucible': 0,
    'shop-onyx': 0,
    'set-lob': 0,
    'promo-lob': 0,
    'set-mrd': 0,
    'promo-mrd': 0,
  }
}

/**
 * New accounts: one of each starter plus a few five-card Master Packs.
 */
export function starterPackCounts(): Record<PackId, number> {
  const next = emptyPackCounts()
  for (const id of STARTER_PACK_IDS) next[id] = 1
  next[SHOP_PACK_ID] = WELCOME_SHOP_PACKS
  return next
}

/**
 * Coerces a JSON blob into non-negative pack counts.
 */
export function sanitizePackCounts(raw: unknown): Record<PackId, number> {
  const next = emptyPackCounts()
  if (!raw || typeof raw !== 'object') return next
  const rec = raw as Record<string, unknown>
  for (const id of PACK_IDS) {
    const n = rec[id]
    next[id] = Number.isInteger(n) && (n as number) > 0 ? (n as number) : 0
  }
  return next
}
